import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    child,
    type DatabaseReference,
    type DataSnapshot,
    onChildAdded,
    onChildChanged,
    onChildRemoved,
} from "firebase/database";
import QuickLRU from "quick-lru";
import { Subject } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { auth } from "@domain/api/firebase/firebase";
import { fetchShallow } from "@domain/api/firebase/firebaseHelpers";

type GroupedCollectionState<T> = {
    state: Map<string, Map<string, T>>;
    isLoaded: boolean;
};

type EntryState<T> = {
    groupId: string;
    subPath: string;
    records: Map<string, T>;
    unsubscribers: (() => void)[];
    expectedGroupIds: Set<string> | null;
    seenGroupIds: Set<string>;
    isLoaded: boolean;
};

// Each entry maps a logical groupId (bankId / kasseId) to a Firebase subPath relative to baseRef.
// Multiple entries can share the same groupId (one per year bucket) - their records are merged
// into the same group map.
export type GroupedEntry = { groupId: string; subPath: string };

export const useGroupedCollectionListener = <T,>(
    baseRef: DatabaseReference,
    entries: GroupedEntry[],
    initializer: (snap: DataSnapshot) => T,
    shouldSkipLoad?: boolean,
    useDebug?: boolean,
): GroupedCollectionState<T> => {
    const publishRef = useRef(new Subject<void>());
    const entriesRef = useRef(new Map<string, EntryState<T>>());
    const initializerRef = useRef(initializer);
    const retainedDroppedEntriesRef = useRef(new QuickLRU<string, GroupedEntry>({ maxSize: 1 }));
    const previousDesiredEntriesRef = useRef(new Map<string, GroupedEntry>());
    const baseRefKeyRef = useRef<string | undefined>(undefined);

    const [state, setState] = useState<Map<string, Map<string, T>>>(new Map());
    const [isLoaded, setIsLoaded] = useState(true);

    const entriesKey = useMemo(() => entries.map((e) => `${e.groupId}:${e.subPath}`).join(","), [entries]);
    const baseRefKey = useMemo(() => baseRef.toString(), [baseRef]);

    useEffect(() => {
        initializerRef.current = initializer;
    }, [initializer]);

    const getEntryKey = useCallback((subPath: string) => `${baseRefKey}|${subPath}`, [baseRefKey]);

    const logLifecycle = useCallback(
        (action: string, payload?: Record<string, unknown>) => {
            if (!useDebug) {
                return;
            }
        },
        [baseRefKey, useDebug],
    );

    const updateLoaded = useCallback(() => {
        const activeEntries = Array.from(entriesRef.current.values());
        setIsLoaded(activeEntries.every((entry) => entry.isLoaded));
    }, []);

    const publishState = useCallback(() => {
        const nextState = new Map<string, Map<string, T>>();
        entriesRef.current.forEach((entry) => {
            const group = nextState.get(entry.groupId) ?? new Map<string, T>();
            entry.records.forEach((record, key) => {
                group.set(key, record);
            });
            nextState.set(entry.groupId, group);
        });
        setState(nextState);
        updateLoaded();
    }, [updateLoaded]);

    const cleanupEntry = useCallback(
        (entry: EntryState<T>) => {
            logLifecycle("offload", { groupId: entry.groupId, subPath: entry.subPath, records: entry.records.size });
            entry.unsubscribers.forEach((fn) => fn());
        },
        [logLifecycle],
    );

    const cleanupAll = useCallback(() => {
        entriesRef.current.forEach(cleanupEntry);
        entriesRef.current.clear();
        retainedDroppedEntriesRef.current.clear();
        previousDesiredEntriesRef.current.clear();
        setState(new Map());
        setIsLoaded(true);
    }, [cleanupEntry]);

    const markEntryLoadedIfReady = useCallback(
        (entry: EntryState<T>) => {
            if (!entry.expectedGroupIds) {
                return;
            }
            const isLoaded = [...entry.expectedGroupIds].every((key) => entry.seenGroupIds.has(key));
            if (isLoaded && !entry.isLoaded) {
                entry.isLoaded = true;
                logLifecycle("load-complete (loaded)", {
                    subPath: entry.subPath,
                    records: entry.records.size,
                });
                updateLoaded();
            }
        },
        [logLifecycle, updateLoaded],
    );

    // Effect: RxJS pipeline
    useEffect(() => {
        const subscription = publishRef.current.pipe(debounceTime(100)).subscribe(publishState);

        return () => {
            subscription.unsubscribe();
        };
    }, [publishState]);

    useEffect(() => {
        return () => {
            entriesRef.current.forEach(cleanupEntry);
            entriesRef.current.clear();
            retainedDroppedEntriesRef.current.clear();
            previousDesiredEntriesRef.current.clear();
        };
    }, [cleanupEntry]);

    // Effect: Firebase listeners
    useEffect(() => {
        if (baseRefKeyRef.current && baseRefKeyRef.current !== baseRefKey) {
            cleanupAll();
        }
        baseRefKeyRef.current = baseRefKey;

        if (!auth.currentUser || shouldSkipLoad || !entries.length) {
            if (entriesRef.current.size > 0) {
                logLifecycle("offload-all", { reason: shouldSkipLoad ? "skip-load" : "no-auth-or-empty" });
            }
            cleanupAll();
            return undefined;
        }

        const currentGroupIds = new Set(entries.map(({ groupId }) => groupId));
        const groupCount = Math.max(1, currentGroupIds.size);
        const retainedLimit = groupCount;
        if (retainedDroppedEntriesRef.current.maxSize !== retainedLimit) {
            retainedDroppedEntriesRef.current.resize(retainedLimit);
        }
        const desiredEntries = new Map<string, GroupedEntry>();
        entries.forEach((entry) => {
            const entryKey = getEntryKey(entry.subPath);
            desiredEntries.set(entryKey, entry);
        });

        previousDesiredEntriesRef.current.forEach((entry, entryKey) => {
            if (desiredEntries.has(entryKey) || !currentGroupIds.has(entry.groupId)) {
                return;
            }
            retainedDroppedEntriesRef.current.delete(entryKey);
            retainedDroppedEntriesRef.current.set(entryKey, entry);
        });

        desiredEntries.forEach((_, entryKey) => {
            retainedDroppedEntriesRef.current.delete(entryKey);
        });
        [...retainedDroppedEntriesRef.current.entries()].forEach(([entryKey, entry]) => {
            if (!currentGroupIds.has(entry.groupId)) {
                retainedDroppedEntriesRef.current.delete(entryKey);
            }
        });
        if (retainedDroppedEntriesRef.current.size > retainedLimit) {
            retainedDroppedEntriesRef.current.resize(retainedLimit);
        }

        const effectiveEntries = [...desiredEntries.entries(), ...retainedDroppedEntriesRef.current.entries()];
        const effectiveEntryKeys = new Set(effectiveEntries.map(([entryKey]) => entryKey));

        const staleActiveEntryKeys: string[] = [];
        entriesRef.current.forEach((entry, entryKey) => {
            if (!currentGroupIds.has(entry.groupId) || !effectiveEntryKeys.has(entryKey)) {
                staleActiveEntryKeys.push(entryKey);
            }
        });
        staleActiveEntryKeys.forEach((entryKey) => {
            const entry = entriesRef.current.get(entryKey);
            if (entry) {
                cleanupEntry(entry);
                entriesRef.current.delete(entryKey);
            }
        });

        effectiveEntries.forEach(([entryKey, { groupId, subPath }]) => {
            if (entriesRef.current.has(entryKey)) {
                return;
            }

            const groupRef = child(baseRef, subPath);
            const entry: EntryState<T> = {
                groupId,
                subPath,
                records: new Map(),
                unsubscribers: [],
                expectedGroupIds: null,
                seenGroupIds: new Set(),
                isLoaded: false,
            };
            entriesRef.current.set(entryKey, entry);
            logLifecycle("load-start", { subPath });
            setIsLoaded(false);

            fetchShallow(groupRef).then((keys) => {
                if (!entriesRef.current.has(entryKey)) {
                    return;
                }
                entry.expectedGroupIds = new Set(Object.keys(keys ?? {}));
                logLifecycle("load-shallow", { subPath, expectedRecords: entry.expectedGroupIds.size });
                if (entry.expectedGroupIds.size === 0) {
                    entry.isLoaded = true;
                    logLifecycle("load-complete (skip)", { subPath, records: 0 });
                    updateLoaded();
                    return;
                }
                markEntryLoadedIfReady(entry);
            });

            const unsubAdded = onChildAdded(groupRef, (snap) => {
                const currentEntry = entriesRef.current.get(entryKey);
                if (!currentEntry) {
                    return;
                }
                currentEntry.records.set(snap.key, initializerRef.current(snap));
                currentEntry.seenGroupIds.add(snap.key);
                markEntryLoadedIfReady(currentEntry);
                publishRef.current.next();
            });
            const unsubChanged = onChildChanged(groupRef, (snap) => {
                const currentEntry = entriesRef.current.get(entryKey);
                if (!currentEntry) {
                    return;
                }
                currentEntry.records.set(snap.key, initializerRef.current(snap));
                currentEntry.seenGroupIds.add(snap.key);
                markEntryLoadedIfReady(currentEntry);
                publishRef.current.next();
            });
            const unsubRemoved = onChildRemoved(groupRef, (snap) => {
                const currentEntry = entriesRef.current.get(entryKey);
                if (!currentEntry) {
                    return;
                }
                currentEntry.records.delete(snap.key);
                currentEntry.seenGroupIds.add(snap.key);
                markEntryLoadedIfReady(currentEntry);
                publishRef.current.next();
            });
            entry.unsubscribers.push(unsubAdded, unsubChanged, unsubRemoved);
        });

        publishRef.current.next();
        updateLoaded();
        previousDesiredEntriesRef.current = desiredEntries;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        baseRef,
        shouldSkipLoad,
        entriesKey,
        getEntryKey,
        baseRefKey,
        cleanupAll,
        cleanupEntry,
        logLifecycle,
        markEntryLoadedIfReady,
        updateLoaded,
    ]);

    return useMemo(
        () => ({
            state,
            isLoaded,
        }),
        [state, isLoaded],
    );
};
